/**
 * 見た目のテーマ(テーマカラーとダークモード)。
 *
 * 端末ごとの好みなので DB ではなく localStorage に置く。ダークモードは
 * 端末側の設定に追従したい人が多く、同期するとかえって不便になるため。
 */

export type ThemeMode = "light" | "dark" | "system";

export interface ThemePrefs {
  /** テーマカラー(16進)。未設定なら既定の深緑 */
  accent?: string;
  mode?: ThemeMode;
}

export const DEFAULT_ACCENT = "#2f6b66";

/** テーマカラーの選択肢。淡い背景・暗い背景の両方で沈まない濃さに揃える */
export const ACCENT_CHOICES: { name: string; value: string }[] = [
  { name: "深緑", value: DEFAULT_ACCENT },
  { name: "梅", value: "#8c5a76" },
  { name: "藍", value: "#3d5a80" },
  { name: "茜", value: "#b04a4a" },
  { name: "琥珀", value: "#a9711d" },
  { name: "桜", value: "#c76e8f" },
];

const STORAGE_KEY = "couples:theme";

export function loadThemePrefs(): ThemePrefs {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return {};
    const prefs = parsed as { accent?: unknown; mode?: unknown };
    return {
      accent: typeof prefs.accent === "string" ? prefs.accent : undefined,
      mode:
        prefs.mode === "light" || prefs.mode === "dark" || prefs.mode === "system"
          ? prefs.mode
          : undefined,
    };
  } catch {
    // 壊れた保存値は既定に戻すだけでよい
    return {};
  }
}

export function saveThemePrefs(prefs: ThemePrefs): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  applyTheme(prefs);
}

function systemPrefersDark(): boolean {
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

/** 保存値を画面に反映する。アクセント色はトークンの上書き、暗さは属性で切り替え */
export function applyTheme(prefs: ThemePrefs): void {
  const root = document.documentElement;
  root.style.setProperty("--color-accent", prefs.accent ?? DEFAULT_ACCENT);

  const mode = prefs.mode ?? "system";
  const dark = mode === "dark" || (mode === "system" && systemPrefersDark());
  if (dark) {
    root.setAttribute("data-theme", "dark");
  } else {
    root.removeAttribute("data-theme");
  }
}

/** 起動時に一度呼ぶ。「端末に合わせる」のときは端末側の切り替えにも追従する */
export function initTheme(): void {
  applyTheme(loadThemePrefs());
  window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change", () => {
    applyTheme(loadThemePrefs());
  });
}
